import React, { useState, useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { css } from '@compiled/react';

import IconButton from '@atlaskit/button/icon/button';
import CrossIcon from '@atlaskit/icon/core/cross';
import Lozenge from '@atlaskit/lozenge/lozenge';
import Link from '@atlaskit/link/link';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Anchor from '@atlaskit/primitives/anchor';
import Box from '@atlaskit/primitives/box';
import Flex from '@atlaskit/primitives/components/flex';
import { Grid } from '@atlaskit/primitives/grid';
import Text from '@atlaskit/primitives/text';
import { xcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';

import { isStableUIEnabledForTests } from '@confluence/e2e-url-override/entry-points/isStableUIEnabledForTests.ts';
import type { TriggerAnalyticsEventType } from '@confluence/editor-features/entry-points/TriggerAnalyticsEventType.ts';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { useIsDarkMode } from '@confluence/app-navigation/entry-points/useIsDarkMode.ts';
import { LazyGlobalFeedbackCollector } from '@confluence/global-feedback-collector/entry-points/GlobalFeedbackCollector.ts';
import { FeedbackSource } from '@confluence/global-feedback-collector/entry-points/feedbackSourceType.ts';
import type { BannerType } from '@confluence/editor-controls-banner-dismissal';
import { useCreationFocusedNavLand } from '@confluence/experiment-cfind-4473-creation-focused-nav/entry-points/useCreationFocusedNavLand.ts';
import {
	useSimplifiedNavNewUsers,
	TREATMENT_COHORTS,
} from '@confluence/experiment-simplify-nav-new-user/entry-points/useSimplifiedNavNewUsers.ts';
import { expValEquals } from '@confluence/feature-experiments';

import {
	ChangeBoardingLight,
	ChangeBoardingDark,
	NewToolbarBannerImageLight,
	NewToolbarBannerImageDark,
} from './ChangeBoardingSvg.tsx';

const layoutArea = 'editor-controls-changeboarding-layout-area';

const feedbackBannerStyles = xcss({
	borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	color: 'color.text.subtle',
	padding: 'space.150',
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
	backgroundColor: 'color.background.accent.purple.subtlest',
	alignItems: 'center',
	justifyItems: 'end',
});

const breakpointContainerQuery = `@container ${layoutArea} (min-width: 67.5rem)`;

const shortChangeBoardingImageAddStyles = css({
	justifySelf: 'start',
	gridArea: 'image',
	display: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values
	[breakpointContainerQuery]: {
		display: 'grid',
	},
});

const feedbackBannerNewPinningStyles = xcss({
	containerType: 'inline-size',
	containerName: layoutArea,
	padding: 'space.0',
});

const feedbackBannerLayerStyles = xcss({
	zIndex: 'modal',
});

const feedbackBannerMessageStyles = xcss({
	gridArea: 'message',
	textAlign: 'center',
	display: 'inline-block',
	alignItems: 'center',
	justifyContent: 'center',
	gap: 'space.100',
	wordBreak: 'break-word',
});

const shortFeedbackBannerMessagePinningStyles = css({
	gridArea: 'message',
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	wordBreak: 'break-word',
	flexWrap: 'wrap',
	padding: token('space.150'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values
	[breakpointContainerQuery]: {
		paddingInline: token('space.150'),
		paddingBlock: token('space.100'),
	},
});

const closeButtonStyles = xcss({
	display: 'flex',
	justifySelf: 'end',
	gridArea: 'close',
	paddingRight: 'space.150',
});

const i18n = defineMessages({
	bannerClose: {
		id: 'editor-action-bar.editor-controls-onboarding.close',
		defaultMessage: 'Close',
		description: 'clicking this icon closes the onboarding banner',
	},
	lozengeMessage: {
		id: 'editor-action-bar.editor-controls-onboarding.lozenge',
		defaultMessage: 'New',
		description: 'This is the message on the lozenge in the editor controls onboarding banner',
	},
	lozengeMessageUpdated: {
		id: 'editor-action-bar.editor-controls-onboarding.lozenge.updated',
		defaultMessage: 'New',
		description: 'Sentence case message on the lozenge in the editor controls onboarding banner',
	},
	collectorCommunityGroup: {
		id: 'editor-action-bar.feedback-collector.community-group',
		defaultMessage: 'Community group',
		description:
			'Clicking this message takes the user to the community group for the Early Access Program.',
	},
	imageAltText: {
		id: 'editor-action-bar.editor-controls-onboarding.image-alt-text',
		defaultMessage: 'New formatting toolbar button',
		description: 'This is the alt text for the image in the editor controls onboarding banner',
	},
	bannerMessageForPinningTopToolbarShort: {
		id: 'editor-action-bar.editor-controls-onboarding.message-pinning-inline-toolbar-short',
		defaultMessage: 'Try the <link>inline toolbar</link>. Unpin it. Select text to edit.',
		description:
			'Shorter message on the editor controls onboarding banner for the new toolbar pinning inline toolbar. It informs the user that the toolbar will be shown when content is selected',
	},
	bannerMessageForPinningInlineToolbarShort: {
		id: 'editor-action-bar.editor-controls-onboarding.message-pinning-top-toolbar-short',
		defaultMessage: '<link>Inline toolbar</link>. Select text to edit. Or pin back to the top.',
		description:
			'Shorter message on the editor controls onboarding banner for the new toolbar pinning top toolbar. It informs the user that the toolbar will be shown when content is selected',
	},
	shareYourFeedbackShort: {
		id: 'editor-action-bar.editor-controls-onboarding.share-your-feedback-short',
		defaultMessage: '<feedback>Feedback</feedback>',
		description: 'Shorter message on the editor controls onboarding banner for sharing feedback',
	},
});

export const LEARN_MORE_LINK_URL =
	'https://support.atlassian.com/confluence-cloud/docs/how-to-use-the-new-toolbar-and-insert-elements/';

export const COMMUNITY_LINK_URL =
	'https://community.atlassian.com/forums/Confluence-articles/Live-Docs-Beta-update-Control-where-the-edit-tool-bar-lives/ba-p/3005144#M13863';

export type EditorControlsOnboardingBannerProps = {
	onDismiss: () => void;
	triggerAnalyticsEvent?: TriggerAnalyticsEventType;
	isLivePage: boolean;
	isEmbeddedConfluence: boolean;
	bannerType: BannerType;
};

const getShortPinningBannerMessage = (bannerType: BannerType) => {
	if (bannerType === 'toolbar-inline') {
		return i18n.bannerMessageForPinningInlineToolbarShort;
	}
	return i18n.bannerMessageForPinningTopToolbarShort;
};

const BannerStyleWrapper = ({ children }: { children: React.ReactNode }) => (
	<div css={shortFeedbackBannerMessagePinningStyles}>{children}</div>
);

const BannerImageStyleWrapper = ({ children }: { children: React.ReactNode }) => (
	<div css={shortChangeBoardingImageAddStyles}>{children}</div>
);

export const EditorControlsBanner = ({
	onDismiss,
	triggerAnalyticsEvent,
	isLivePage,
	isEmbeddedConfluence,
	bannerType,
}: EditorControlsOnboardingBannerProps): React.JSX.Element | null => {
	const isDarkMode = useIsDarkMode();
	const { formatMessage } = useIntl();
	const [showFeedbackCollector, setShowFeedbackCollector] = useState<boolean>(false);
	const { areBannersHidden: areBannersHiddenForLandSimplifyNav } = useCreationFocusedNavLand();
	const { navStyle, source } = useSimplifiedNavNewUsers();
	const areBannersHidden =
		areBannersHiddenForLandSimplifyNav || (source === 'xflow' && TREATMENT_COHORTS.has(navStyle));

	useEffect(() => {
		if (triggerAnalyticsEvent) {
			void triggerAnalyticsEvent('controls.banner.displayed', {
				isLivePage,
			});
		}
	}, [triggerAnalyticsEvent, isLivePage]);

	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();

	const handleOnClickClose = () => {
		if (triggerAnalyticsEvent) {
			void triggerAnalyticsEvent('controls.banner.dismissed', {
				isLivePage,
			});
		}
		onDismiss();
	};

	const handleOnClickFeedbackButton = () => {
		setShowFeedbackCollector(true);
	};

	const handleSubmitFeedback = () => {
		handleOnClickClose();
		setShowFeedbackCollector(false);
	};

	if (isEmbeddedConfluence) {
		return null;
	}

	if (
		isStableUIEnabledForTests() &&
		expValEquals('platform_editor_stabilise_ssr_vr_tests', 'isEnabled', true)
	) {
		return null;
	}

	const isNewToolbarEnabled = true;

	const NewToolbarBannerImage = isDarkMode ? NewToolbarBannerImageDark : NewToolbarBannerImageLight;

	const ChangeBoardingImage = isDarkMode ? ChangeBoardingDark : ChangeBoardingLight;

	const BannerImage = isNewToolbarEnabled ? NewToolbarBannerImage : ChangeBoardingImage;

	if (areBannersHidden) {
		return null;
	}

	return (
		<div data-vc="editor-controls-onboarding-banner" {...ssrPlaceholderIdProp}>
			<Grid
				templateAreas={['image message close']}
				templateColumns="minmax(max-content, 1fr) auto 1fr"
				xcss={[feedbackBannerStyles, feedbackBannerLayerStyles, feedbackBannerNewPinningStyles]}
				testId="editor-controls-onboarding-banner"
			>
				<BannerImageStyleWrapper>
					<BannerImage
						title={formatMessage(i18n.imageAltText)}
						titleId={`onboarding-banner-image-${isDarkMode ? 'dark' : 'light'}`}
					/>
				</BannerImageStyleWrapper>
				<BannerStyleWrapper>
					<Flex xcss={feedbackBannerMessageStyles}>
						<Lozenge appearance="new" isBold>
							{formatMessage(i18n.lozengeMessageUpdated)}
						</Lozenge>{' '}
						<FormattedMessage
							{...getShortPinningBannerMessage(bannerType)}
							values={{
								link: (chunks: React.ReactNode[]) => (
									<Anchor href={LEARN_MORE_LINK_URL} target="_blank">
										{chunks}
									</Anchor>
								),
								strong: (chunks: React.ReactNode[]) => <Text weight="bold">{chunks}</Text>,
							}}
						/>{' '}
						<FormattedMessage
							{...i18n.shareYourFeedbackShort}
							values={{
								feedback: (chunks: React.ReactNode[]) => (
									// eslint-disable-next-line @atlassian/a11y/anchor-is-valid
									<Link href="#" onClick={handleOnClickFeedbackButton}>
										{chunks}
									</Link>
								),
							}}
						/>
					</Flex>
				</BannerStyleWrapper>
				<Box xcss={closeButtonStyles}>
					<IconButton
						appearance="subtle"
						spacing="compact"
						icon={CrossIcon}
						label={formatMessage(i18n.bannerClose)}
						onClick={handleOnClickClose}
					/>
				</Box>
			</Grid>
			{showFeedbackCollector && (
				<LazyGlobalFeedbackCollector
					onClose={() => setShowFeedbackCollector(false)}
					onCloseComplete={() => {}}
					onSubmit={handleSubmitFeedback}
					feedbackSource={FeedbackSource.EDITOR_CONTROLS}
					additionalFields={{
						source: 'editor-controls-feedback-banner',
					}}
				/>
			)}
		</div>
	);
};
